import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button'
import { FrequencyBuilder } from '@/components/FrequencyBuilder'
import { FrequencyPattern } from '@/types/challenge'
import type { Challenge } from '@/types/challenge'

interface FrequencyConfigurationStepProps {
  selectedChallenge: Challenge | null
  customTitle: string
  frequency: FrequencyPattern
  onTitleChange: (title: string) => void
  onFrequencyChange: (frequency: FrequencyPattern) => void
  onNext: () => void
  onBack: () => void
}

export function FrequencyConfigurationStep({
  selectedChallenge,
  customTitle,
  frequency,
  onTitleChange,
  onFrequencyChange,
  onNext,
  onBack
}: FrequencyConfigurationStepProps) {
  const { t } = useTranslation()
  const [localTitle, setLocalTitle] = useState(customTitle)

  useEffect(() => {
    setLocalTitle(customTitle)
  }, [customTitle])

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    setLocalTitle(newTitle)
    onTitleChange(newTitle)
  }

  const canProceed = selectedChallenge ? true : (localTitle.trim().length >= 3 && localTitle.trim().length <= 60)

  const validateFrequency = () => {
    if (frequency.mode === 'daily' && (!frequency.times || frequency.times.length === 0)) {
      return false
    }
    if (frequency.mode === 'weekly' && (!frequency.daysOfWeek || frequency.daysOfWeek.length === 0)) {
      return false
    }
    return true
  }

  // Ensure daily mode has at least 9am if no times are set
  useEffect(() => {
    if (frequency.mode === 'daily' && (!frequency.times || frequency.times.length === 0)) {
      onFrequencyChange({
        ...frequency,
        times: ['09:00']
      })
    }
  }, [frequency.mode, frequency.times, onFrequencyChange])

  const handleNext = () => {
    if (canProceed && validateFrequency()) {
      onNext()
    }
  }

  return (
    <div className="space-y-xl">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-text mb-sm">
          {selectedChallenge ? t('challenges.configureFrequency') : t('challenges.createYourChallenge')}
        </h1>
        <p className="text-text-muted">
          {selectedChallenge 
            ? t('challenges.setWhenReminded', { name: selectedChallenge.name })
            : t('challenges.giveChallengeName')
          }
        </p>
      </div>

      {/* Challenge Title (only for custom challenges) */}
      {!selectedChallenge && (
        <div className="space-y-md">
          <h3 className="text-lg font-medium text-text">{t('challenges.challengeTitle')}</h3>
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-text mb-sm">
              {t('challenges.whatToAchieve')}
            </label>
            <input
              type="text"
              id="title"
              value={localTitle}
              onChange={handleTitleChange}
              placeholder={t('challenges.exampleChallenge')}
              className="w-full px-lg py-md border border-border rounded-xl bg-bg text-text placeholder-text-faint focus:outline-none focus:ring-2 focus:ring-focus"
              maxLength={60}
              required
            />
            <p className="text-xs text-text-faint mt-xs">
              {localTitle.length}/60 {t('common.characters')}
            </p>
            {localTitle.trim().length > 0 && localTitle.trim().length < 3 && (
              <p className="text-xs text-error mt-xs">
                {t('errors.challengeTitleMinLength')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Selected Challenge Display (only for catalog challenges) */}
      {selectedChallenge && (
        <div className="bg-surface border border-border rounded-xl p-lg">
          <h3 className="text-lg font-medium text-text mb-sm">
            {t('challenges.selectedChallenge')}
          </h3>
          <div>
            <h4 className="font-medium text-text">{selectedChallenge.name}</h4>
            <p className="text-sm text-text-muted mt-xs">
              {selectedChallenge.description}
            </p>
          </div>
        </div>
      )}

      {/* Frequency Configuration */}
      <div className="space-y-md">
        <h3 className="text-lg font-medium text-text">{t('challenges.reminderFrequency')}</h3>
        <p className="text-sm text-text-muted">
          {selectedChallenge 
            ? t('challenges.prefilledSchedule')
            : t('challenges.whenToBeReminded')
          }
        </p>
        <FrequencyBuilder value={frequency} onChange={onFrequencyChange} />
      </div>

      {/* Validation Messages */}
      {frequency.mode === 'daily' && (!frequency.times || frequency.times.length === 0) && (
        <div className="bg-warning/10 border border-warning rounded-xl p-md">
          <p className="text-sm text-warning">
            {t('errors.selectAtLeastOneTime')}
          </p>
        </div>
      )}

      {frequency.mode === 'weekly' && (!frequency.daysOfWeek || frequency.daysOfWeek.length === 0) && (
        <div className="bg-warning/10 border border-warning rounded-xl p-md">
          <p className="text-sm text-warning">
            {t('errors.selectAtLeastOneDay')}
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-lg">
        <Button
          type="button"
          variant="secondary"
          onClick={onBack}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('common.back')}
        </Button>
        <Button
          onClick={handleNext}
          disabled={!canProceed || !validateFrequency()}
        >
          {t('common.continue')}
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  )
} 